import type { Org, OrgCapability } from '@shopgnd/shared';
import type { HubAccessFlags } from '@/lib/hubNavigation';

import { isSellerHubPath, vendorHub } from '@/lib/vendorHubPaths';
import { orgHasSellerCapabilities } from '@/lib/workspaceNavigation';

export type HubEntryProfiles = {
  vendorId?: string | null;
  proId?: string | null;
};

/**
 * Business Center entry route.
 * Platform admins land in Administration first; store/pro workspaces are one click away.
 * Avoids sending admin-only users to seller home (blank redirect loop).
 */
export function hubEntryPath(
  hub: HubAccessFlags,
  org: Pick<Org, 'capabilities'> | null,
  profiles?: HubEntryProfiles,
): string {
  const caps = org?.capabilities ?? [];
  const hasVendor = Boolean(profiles?.vendorId);
  const hasPro = Boolean(profiles?.proId);
  const hasOrg = Boolean(org);

  // Admins: always open Administration unless they are mid–account-view (handled elsewhere).
  if (hub.hasAdminHub) return '/hub/admin';

  // Profile exists but Org row not loaded yet — avoid sending users to org-gated routes in a loop.
  if ((hasVendor || hasPro) && !hasOrg) {
    if (hasPro) return '/hub/pro';
    return vendorHub.home;
  }

  if (hasVendor) {
    if (caps.includes('shop')) return vendorHub.home;
    if (caps.includes('property')) return vendorHub.properties;
    if (caps.includes('classifieds')) return vendorHub.classifieds;
    return vendorHub.home;
  }

  if (hasPro) {
    if (caps.includes('bookings')) return '/hub/pro/bookings';
    return '/hub/pro';
  }

  if (orgHasSellerCapabilities(caps) || hub.hasVendorHub) {
    if (caps.includes('shop')) return vendorHub.home;
    if (caps.includes('property')) return vendorHub.properties;
    if (caps.includes('classifieds')) return vendorHub.classifieds;
    return vendorHub.home;
  }

  if (caps.includes('bookings') || hub.hasProHub) {
    if (caps.includes('bookings')) return '/hub/pro/bookings';
    return '/hub/pro';
  }

  return '/hub';
}

/** @deprecated Use hubEntryPath — kept as alias during migration. */
export function hubLandingPath(
  hub: HubAccessFlags,
  org: Pick<Org, 'capabilities'> | null,
  profiles?: HubEntryProfiles,
): string {
  return hubEntryPath(hub, org, profiles);
}

/** Where to send users who opened /hub without a loaded workspace profile yet. */
export function hubAccessDeniedRedirectForUser(roles: import('@shopgnd/shared').UserRole[]): string {
  const safeRoles = roles ?? [];
  if (safeRoles.includes('vendor') || safeRoles.includes('service_provider')) {
    return vendorHub.home;
  }
  if (safeRoles.includes('admin') || safeRoles.includes('platform_admin')) {
    return '/hub/admin';
  }
  return '/account';
}

/** Safe fallback when vendor routes are not usable (no Vendor row). */
export function hubFallbackPath(
  hub: HubAccessFlags,
  org: Pick<Org, 'capabilities'> | null,
  profiles?: HubEntryProfiles,
): string {
  const entry = hubEntryPath(hub, org, profiles);
  if (!profiles?.vendorId && isSellerHubPath(entry) && entry !== '/hub') {
    if (hub.hasAdminHub) return '/hub/admin';
    if (hub.hasProHub || org?.capabilities?.includes('bookings')) return '/hub/pro';
  }
  return entry;
}

/** Where to send users who cannot access the current hub route (avoids /hub ↔ deep-link loops). */
export function hubAccessDeniedPath(
  hub: HubAccessFlags,
  capabilities: OrgCapability[],
  profiles?: HubEntryProfiles,
): string {
  if (profiles?.vendorId && capabilities.includes('shop')) return vendorHub.home;
  if (profiles?.vendorId) return vendorHub.home;
  if (profiles?.proId) return '/hub/pro';
  if (hub.hasAdminHub) return '/hub/admin';
  return hubEntryPath(hub, { capabilities }, profiles);
}
